import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import useSWR from "swr";
import {Badge, Button, Drawer, Group, Select, SimpleGrid, Stack, Tabs, Text, Textarea} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { fetcher } from "../../../services/fetcher";
import { SalesLeads_Api } from "../../../api/endpoints";
import { errorNotifications, successNotifications } from "../../../utils/handleNotifications";
import { STATUS_OPTIONS } from "../utils/leadsOptions";
import LeadFollowups from "./LeadFollowups";
import LeadForm from "./LeadForm";
import { LeadActivityTimeline, LeadEventTimeline } from "./LeadTimeline";

const asArray = (value) => (Array.isArray(value) ? value : []);

const unwrapLead = (raw) => {
  if (!raw || typeof raw !== "object") return {};
  const nested = raw.data ?? raw.lead;
  return nested && typeof nested === "object" && !Array.isArray(nested)
    ? { ...raw, ...nested }
    : raw;
};

const sortByCreated = (items) =>
  [...items].sort(
    (a, b) =>
      dayjs(a.created_on || a.activity_date).valueOf() -
      dayjs(b.created_on || b.activity_date).valueOf()
  );

const sortByDueDate = (items) =>
  [...items].sort((a, b) => dayjs(a.due_date).valueOf() - dayjs(b.due_date).valueOf());

const LeadDrawer = ({ opened, onClose, lead, initialTab = "activities", onLeadUpdated }) => {
  const [drawerTab, setDrawerTab] = useState("activities");
  const [localLead, setLocalLead] = useState(lead);
  const [isActivitySubmitting, setIsActivitySubmitting] = useState(false);
  const [isFollowupSubmitting, setIsFollowupSubmitting] = useState(false);
  const [activityForm, setActivityForm] = useState({
    activity_type: "CALL",
    status: "DONE",
    activity_date: new Date(),
    summary: "",
  });
  const [followupForm, setFollowupForm] = useState({
    person_name: "",
    phone: "",
    due_date: null,
    followup_type: "OUTBOUND_CALL",
    priority: "MEDIUM",
    trek: "",
    notes: "",
  });

  const leadId = localLead?.id;

  const { data: leadDetail, isLoading: isLeadLoading, mutate: mutateLead } = useSWR(
    opened && leadId ? `${SalesLeads_Api}${leadId}/` : null,
    fetcher,
    { revalidateOnFocus: false, shouldRetryOnError: false }
  );

  const { data: timelineData, isLoading: isTimelineLoading } = useSWR(
    opened && drawerTab === "timeline" && leadId
      ? `${SalesLeads_Api}${leadId}/timeline/?page_size=50`
      : null,
    fetcher,
    { revalidateOnFocus: false, shouldRetryOnError: false }
  );

  const resolvedLead = useMemo(() => {
    if (!leadId) return localLead;
    if (!leadDetail) return localLead;
    return { ...localLead, ...unwrapLead(leadDetail) };
  }, [leadId, localLead, leadDetail]);

  const activitiesForLead = useMemo(() => {
    const fromApi = asArray(resolvedLead?.total_activities).length
      ? asArray(resolvedLead.total_activities)
      : asArray(resolvedLead?.activities);
    return sortByCreated(fromApi);
  }, [resolvedLead]);

  const followupsForLead = useMemo(
    () => sortByDueDate(asArray(resolvedLead?.followups)),
    [resolvedLead]
  );

  useEffect(() => {
    if (!opened) return;
    setDrawerTab(initialTab || "activities");
  }, [initialTab, opened]);

  useEffect(() => {
    setLocalLead(lead);
    setActivityForm({
      activity_type: "CALL",
      status: "DONE",
      activity_date: new Date(),
      summary: "",
    });
    setFollowupForm({
      person_name: lead?.name || "",
      phone: lead?.phone || "",
      due_date: null,
      followup_type: "OUTBOUND_CALL",
      priority: "MEDIUM",
      trek: lead?.trek || "",
      notes: "",
    });
  }, [lead]);

  const handleActivitySubmit = async (event) => {
    event.preventDefault();
    if (!leadId) return;

    setIsActivitySubmitting(true);
    try {
      await fetcher(`crm/leads/${leadId}/activities/`, "POST", {
        activity_type: activityForm.activity_type,
        status: activityForm.status,
        activity_date: activityForm.activity_date
          ? dayjs(activityForm.activity_date).format("YYYY-MM-DD")
          : null,
        summary: activityForm.summary,
      });
      setActivityForm((prev) => ({ ...prev, summary: "" }));
      await mutateLead();
      successNotifications("Activity logged");
    } catch (error) {
      errorNotifications(error?.response?.data || "Failed to log activity", error);
    } finally {
      setIsActivitySubmitting(false);
    }
  };

  const handleFollowupSubmit = async (event) => {
    event.preventDefault();
    if (!leadId) return;

    setIsFollowupSubmitting(true);
    try {
      await fetcher(`crm/leads/${leadId}/followups/`, "POST", {
        person_name: followupForm.person_name,
        phone: followupForm.phone,
        due_date: followupForm.due_date
          ? dayjs(followupForm.due_date).format("YYYY-MM-DD")
          : null,
        followup_type: followupForm.followup_type,
        priority: followupForm.priority,
        trek: followupForm.trek,
        notes: followupForm.notes,
      });
      setFollowupForm((prev) => ({ ...prev, due_date: null, notes: "" }));
      await mutateLead();
      successNotifications("Follow-up scheduled");
    } catch (error) {
      errorNotifications(error?.response?.data || "Failed to schedule follow-up", error);
    } finally {
      setIsFollowupSubmitting(false);
    }
  };

  const handleLeadUpdate = async (payload) => {
    if (!leadId) throw new Error("Lead not found");
    const updated = await fetcher(`crm/leads/${leadId}/`, "PATCH", payload);
    const nextLead = { ...localLead, ...updated, ...payload };
    setLocalLead(nextLead);
    await mutateLead();
    successNotifications("Lead updated");
    if (typeof onLeadUpdated === "function") await onLeadUpdated();
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="45%"
      withCloseButton
      title={
        <Stack gap={4}>
          <Text size="xs" c="dimmed">
            {resolvedLead?.lead_no || `LID-${leadId ?? ""}`}
          </Text>
          <Text fw={700} size="lg">
            {resolvedLead?.name || "Lead Details"}
          </Text>
        </Stack>
      }
    >
      <Stack gap="sm">
        <Group gap={6}>
          <Badge variant="light" color="blue" size="sm">{resolvedLead?.status || "NEW"}</Badge>
          <Badge variant="light" color="gray" size="sm">{resolvedLead?.source || "—"}</Badge>
          <Badge variant="light" color="teal" size="sm">{resolvedLead?.lead_type || "—"}</Badge>
        </Group>
        <Group gap="sm" align="center">
          <Text size="sm" c="dimmed">Status:</Text>
          <Select
            data={STATUS_OPTIONS}
            value={localLead?.status || "NEW"}
            onChange={(value) =>
              setLocalLead((prev) => (prev ? { ...prev, status: value || prev.status } : prev))
            }
            size="xs"
            maw={140}
          />
        </Group>
        <Text size="sm" c="dimmed">
          {resolvedLead?.phone || "—"} · {resolvedLead?.email || "—"} · Activity:{" "}
          {resolvedLead?.trek || "—"} · {resolvedLead?.persons || "—"} pax
        </Text>

        <Tabs value={drawerTab} onChange={setDrawerTab} variant="default">
          <Tabs.List>
            <Tabs.Tab value="activities">Activities</Tabs.Tab>
            <Tabs.Tab value="followups">Follow-ups</Tabs.Tab>
            <Tabs.Tab value="timeline">Timeline</Tabs.Tab>
            <Tabs.Tab value="edit">Edit Lead</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="activities" pt="md">
            <Stack gap="lg">
              <LeadActivityTimeline
                items={activitiesForLead}
                isLoading={isLeadLoading && !activitiesForLead.length}
              />
              <Stack gap="sm">
                <Text fw={600}>Log Activity</Text>
                <form onSubmit={handleActivitySubmit}>
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                    <Select
                      label="Activity Type"
                      withAsterisk
                      data={[
                        { value: "CALL", label: "Call" },
                        { value: "MEETING", label: "Meeting" },
                        { value: "EMAIL", label: "Email" },
                        { value: "WHATSAPP", label: "WhatsApp" },
                        { value: "OTHER", label: "Other" },
                      ]}
                      value={activityForm.activity_type}
                      onChange={(value) =>
                        setActivityForm((prev) => ({ ...prev, activity_type: value || "CALL" }))
                      }
                      required
                    />
                    <Select
                      label="Status"
                      withAsterisk
                      data={[
                        { value: "DONE", label: "Done" },
                        { value: "SCHEDULED", label: "Scheduled" },
                        { value: "PENDING", label: "Pending" },
                      ]}
                      value={activityForm.status}
                      onChange={(value) =>
                        setActivityForm((prev) => ({ ...prev, status: value || "DONE" }))
                      }
                      required
                    />
                    <DatePickerInput
                      label="Date"
                      withAsterisk
                      value={activityForm.activity_date}
                      onChange={(value) =>
                        setActivityForm((prev) => ({ ...prev, activity_date: value }))
                      }
                      required
                    />
                  </SimpleGrid>
                  <Textarea
                    label="Summary"
                    withAsterisk
                    placeholder="What was discussed or done..."
                    value={activityForm.summary}
                    onChange={(e) =>
                      setActivityForm((prev) => ({ ...prev, summary: e.target.value }))
                    }
                    required
                    mt="sm"
                  />
                  <Group justify="flex-end" mt="md">
                    <Button type="submit" color="orange" loading={isActivitySubmitting}>
                      Add Activity
                    </Button>
                  </Group>
                </form>
              </Stack>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="followups" pt="md">
            <LeadFollowups
              followups={followupsForLead}
              isLoading={isLeadLoading && !followupsForLead.length}
              followupForm={followupForm}
              onFollowupFormChange={(updates) =>
                setFollowupForm((prev) => ({ ...prev, ...updates }))
              }
              onSubmit={handleFollowupSubmit}
              isSubmitting={isFollowupSubmitting}
            />
          </Tabs.Panel>

          <Tabs.Panel value="timeline" pt="md">
            <LeadEventTimeline items={timelineData?.results} isLoading={isTimelineLoading} />
          </Tabs.Panel>

          <Tabs.Panel value="edit" pt="md">
            {drawerTab === "edit" && leadId ? (
              <LeadForm
                lead={resolvedLead}
                onSubmit={handleLeadUpdate}
                submitLabel="Update Lead"
              />
            ) : null}
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Drawer>
  );
};

export default LeadDrawer;
